import React, { useEffect, useId, useRef } from 'react';
import clsx from 'clsx';
import Button from './Button';
import styles from './Patterns.module.scss';

export default function Modal({ open, onClose, title, closeLabel = 'Close dialog', children, footer, theme = 'light', className }) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const previousFocus = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const activeElement = document.activeElement;
    const focusableOutsideDialog = Array.from(document.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .find(element => !dialogRef.current?.contains(element));
    previousFocus.current = activeElement && activeElement !== document.body && !dialogRef.current?.contains(activeElement)
      ? activeElement
      : focusableOutsideDialog;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const firstFocusable = dialogRef.current?.querySelector('input, select, textarea, [href], button:not([data-modal-close]), [tabindex]:not([tabindex="-1"])');
    (firstFocusable || dialogRef.current)?.focus();
    const handleKeyDown = event => {
      if (event.key === 'Escape') onClose?.();
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus.current?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className={styles.modalBackdrop} onMouseDown={event => event.target === event.currentTarget && onClose?.()}>
      <section ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby={titleId}
        tabIndex="-1" data-ds-theme={theme} className={clsx(styles.modal, className)}>
        <div className={styles.modalHeader}>
          <h2 id={titleId}>{title}</h2>
          <Button variant="ghost" onClick={onClose} aria-label={closeLabel} data-modal-close><span aria-hidden="true">×</span></Button>
        </div>
        <div className={styles.modalBody}>{children}</div>
        {footer && <div className={styles.modalFooter}>{footer}</div>}
      </section>
    </div>
  );
}
